import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Church, MapPin, CalendarDays, Building2, Plus, X, Loader2, Clock, Check, ShieldCheck, ThumbsUp, ThumbsDown } from "lucide-react";
import { useMemberAuth } from "@/members/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

const STATUS_LABELS = {
  pending: { label: "Em análise", icon: Clock, klass: "text-[hsl(var(--accent))]" },
  approved: { label: "Aprovado", icon: Check, klass: "text-[hsl(var(--primary))]" },
  rejected: { label: "Rejeitado", icon: X, klass: "text-[hsl(var(--muted-foreground))]" },
};

export default function MemberShrines() {
  const { member } = useMemberAuth();
  const isAssessor = member?.ciees_role === "assessor_espiritual";
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [toValidate, setToValidate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/members/shrines`, { withCredentials: true }),
        axios.get(`${API}/members/shrines/mine`, { withCredentials: true }),
      ];
      if (isAssessor) {
        requests.push(axios.get(`${API}/members/shrines/to-validate`, { withCredentials: true }));
      }
      const results = await Promise.all(requests);
      setItems(results[0].data.items || []);
      setMine(results[1].data.items || []);
      if (isAssessor) setToValidate(results[2].data.items || []);
    } catch (e) {
      toast.error("Erro a carregar santuários.");
    } finally {
      setLoading(false);
    }
  }, [isAssessor]);

  useEffect(() => { load(); }, [load]);

  const validate = async (id) => {
    if (!window.confirm("Validar esta proposta? Ficará aprovada e visível para todos os sócios.")) return;
    try {
      await axios.post(`${API}/members/shrines/${id}/assessor-validate`, {}, { withCredentials: true });
      toast.success("Proposta validada — o sócio foi notificado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a validar.");
    }
  };

  const rejectByAssessor = async (id) => {
    if (!window.confirm("Rejeitar esta proposta? Este passo é definitivo.")) return;
    try {
      await axios.post(`${API}/members/shrines/${id}/assessor-reject`, {}, { withCredentials: true });
      toast.success("Proposta rejeitada — o sócio foi notificado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a rejeitar.");
    }
  };

  return (
    <section className="container-page py-12 lg:py-16" data-testid="member-shrines-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Santuários Empreendedores</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Santuários nas empresas.</h1>
          <span className="accent-rule mt-6 block" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
            Cada Santuário Empreendedor é um sinal vivo da Aliança de Amor no quotidiano das nossas empresas.
            Conheça os santuários da nossa comunidade ou proponha um novo.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary"
          data-testid="member-shrines-propose-btn"
        >
          <Plus className="h-4 w-4" /> Propor santuário
        </button>
      </div>

      {/* Propostas a validar — visível apenas a Assessores Espirituais */}
      {isAssessor && toValidate.length > 0 && (
        <div className="mt-10 bg-white border-l-2 border-[hsl(var(--accent))] border-r border-t border-b border-[hsl(var(--border))]" data-testid="assessor-to-validate-section">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" />
            <h2 className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
              Propostas a validar como Assessor Espiritual
              <span className="ml-2 normal-case tracking-normal text-[hsl(var(--muted-foreground))]">({toValidate.length})</span>
            </h2>
          </div>
          <ul className="divide-y divide-[hsl(var(--border))]">
            {toValidate.map((s) => (
              <li key={s.id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-4" data-testid={`to-validate-${s.id}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Church className="h-5 w-5 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                      Proposto por <strong className="text-[hsl(var(--foreground))]">{s.submitted_by_member_name || "Sócio"}</strong>
                      {s.host_company ? ` · ${s.host_company}` : ""}
                      {s.city ? ` · ${s.city}` : ""}
                      {s.inauguration_date ? ` · inauguração ${s.inauguration_date}` : ""}
                    </div>
                    {s.description_pt && (
                      <p className="mt-1.5 text-xs italic text-[hsl(var(--muted-foreground))] line-clamp-2 leading-relaxed">
                        “{s.description_pt}”
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => validate(s.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-[hsl(var(--primary))] text-white text-[11px] uppercase tracking-[0.16em] hover:bg-[#093630]"
                    data-testid={`assessor-validate-${s.id}`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" /> Validar
                  </button>
                  <button
                    onClick={() => rejectByAssessor(s.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[hsl(var(--border))] text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))] hover:text-red-700 hover:border-red-700"
                    data-testid={`assessor-reject-${s.id}`}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" /> Rejeitar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Minhas propostas */}
      {mine.length > 0 && (
        <div className="mt-10" data-testid="member-shrines-mine-section">
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">As minhas propostas</h2>
          <ul className="mt-3 space-y-2">
            {mine.map((s) => {
              const cfg = STATUS_LABELS[s.status] || STATUS_LABELS.pending;
              const Icon = cfg.icon;
              return (
                <li key={s.id} className="flex items-center justify-between gap-3 bg-white border border-[hsl(var(--border))] px-4 py-3 text-sm" data-testid={`mine-shrine-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Church className="h-4 w-4 text-[hsl(var(--accent))] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate">{s.name}</div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{s.host_company}{s.city ? ` · ${s.city}` : ""}</div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] ${cfg.klass}`}>
                    <Icon className="h-3 w-3" /> {cfg.label}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Grelha aprovados */}
      <div className="mt-10">
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-12 text-center" data-testid="member-shrines-empty">
            <Church className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              Ainda não há santuários aprovados.
            </p>
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Seja o primeiro a propor.</p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="member-shrines-grid">
            {items.map((s) => (
              <li key={s.id} className="bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] transition-colors flex flex-col overflow-hidden" data-testid={`shrine-card-${s.slug}`}>
                {s.photo_url ? (
                  <div className="aspect-[4/3] bg-[hsl(var(--secondary))] overflow-hidden">
                    <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[hsl(var(--primary))] flex items-center justify-center">
                    <Church className="h-12 w-12 text-[hsl(var(--accent))]" />
                  </div>
                )}
                <div className="p-5 flex flex-col gap-2 flex-1">
                  <h3 className="display-heading text-lg leading-tight">{s.name}</h3>
                  <dl className="text-xs space-y-1 text-[hsl(var(--muted-foreground))]">
                    <Field icon={Building2}>{s.host_company}</Field>
                    {(s.address || s.city) && (
                      <Field icon={MapPin}>{[s.address, s.city].filter(Boolean).join(", ")}</Field>
                    )}
                    {s.inauguration_date && (
                      <Field icon={CalendarDays}>Inaugurado a {formatDate(s.inauguration_date)}</Field>
                    )}
                  </dl>
                  {s.description_pt && (
                    <p className="mt-2 text-xs italic text-[hsl(var(--muted-foreground))] line-clamp-3 leading-relaxed">
                      “{s.description_pt}”
                    </p>
                  )}
                  {s.responsible_member_name && (
                    <p className="mt-auto pt-3 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]">
                      Sócio responsável · <span className="normal-case tracking-normal text-[hsl(var(--foreground))]">{s.responsible_member_name}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showForm && (
        <ProposeShrineDialog
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
    </section>
  );
}

const Field = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2">
    <Icon className="h-3 w-3 mt-0.5 flex-shrink-0 text-[hsl(var(--accent))]" />
    <span className="break-words">{children}</span>
  </div>
);

function ProposeShrineDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", host_company: "", address: "", city: "", country: "Portugal",
    inauguration_date: "", description_pt: "", assessor_member_id: "",
  });
  const [assessors, setAssessors] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/members/shrines/assessors`, { withCredentials: true });
        setAssessors(r.data.items || []);
      } catch { toast.error("Erro a carregar assessores."); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host_company.trim()) {
      toast.error("Preencha nome e empresa anfitriã.");
      return;
    }
    if (!form.assessor_member_id) {
      toast.error("Escolha o Assessor Espiritual que vai validar a proposta.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/members/shrines/propose`, form, { withCredentials: true });
      toast.success("Proposta enviada! O Assessor recebeu uma notificação.");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a submeter proposta.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto" data-testid="propose-shrine-dialog">
      <form onSubmit={submit} className="bg-white max-w-xl w-full border border-[hsl(var(--border))] my-8">
        <div className="px-7 py-6 border-b border-[hsl(var(--border))] flex items-start justify-between">
          <div>
            <div className="overline">Santuários Empreendedores</div>
            <h3 className="display-heading text-2xl mt-2">Propor um novo santuário.</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] -mr-2 p-1" data-testid="propose-shrine-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-7 py-6 space-y-5">
          <DlgInput label="Nome do santuário *" value={form.name} onChange={(v) => set("name", v)} placeholder="Santuário KW Lead Restelo" testid="prop-name" required />
          <DlgInput label="Empresa anfitriã *" value={form.host_company} onChange={(v) => set("host_company", v)} placeholder="KW Lead Restelo" testid="prop-host" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <DlgInput label="Morada" value={form.address} onChange={(v) => set("address", v)} testid="prop-address" />
            <DlgInput label="Cidade" value={form.city} onChange={(v) => set("city", v)} testid="prop-city" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <DlgInput label="País" value={form.country} onChange={(v) => set("country", v)} testid="prop-country" />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Data de inauguração</label>
              <input type="date" value={form.inauguration_date} onChange={(e) => set("inauguration_date", e.target.value)}
                     className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                     data-testid="prop-date" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Descrição</label>
            <textarea rows={4} value={form.description_pt} onChange={(e) => set("description_pt", e.target.value)}
                      placeholder="Breve história, padroeiro, momentos especiais..."
                      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                      data-testid="prop-description" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Assessor Espiritual *</label>
            <select
              value={form.assessor_member_id}
              onChange={(e) => set("assessor_member_id", e.target.value)}
              required
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="prop-assessor"
            >
              <option value="">— escolher Assessor —</option>
              {assessors.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
              O Assessor receberá um e-mail para validar a proposta. A validação dele equivale à aprovação da Direção.
            </p>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--secondary))] p-3 border-l-2 border-[hsl(var(--accent))]">
            Basta uma das aprovações (Assessor Espiritual ou Direção) para o santuário ficar visível para os outros sócios.
          </p>
        </div>
        <div className="px-7 py-5 bg-[hsl(var(--secondary))] flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="propose-shrine-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="propose-shrine-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? "A enviar…" : "Enviar proposta"}
          </button>
        </div>
      </form>
    </div>
  );
}

const DlgInput = ({ label, value, onChange, testid, placeholder, required }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
    <input
      type="text" value={value || ""} placeholder={placeholder || ""} required={!!required}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    />
  </div>
);
